import { useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Users, RefreshCw, Linkedin, Mail } from "lucide-react";

const scoreTone = (s) => (s >= 75 ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : s >= 50 ? "bg-amber-500/15 text-amber-400 border-amber-500/30" : "bg-rose-500/15 text-rose-400 border-rose-500/30");

const ApplicantCard = ({ a, stages, onStage, onScore, scoring }) => {
  const c = a.candidate || {};
  return (
    <Card className="p-4 space-y-3" data-testid={`applicant-${a.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium truncate">{c.full_name || "Candidate"}</div>
          <div className="text-xs text-muted-foreground line-clamp-2">{c.headline || c.current_role}</div>
        </div>
        {a.fit ? (
          <Badge variant="outline" className={`font-mono shrink-0 ${scoreTone(a.fit.score)}`} data-testid={`applicant-score-${a.id}`}>{a.fit.score}</Badge>
        ) : (
          <Badge variant="outline" className="text-[10px] shrink-0" data-testid={`applicant-score-${a.id}`}>scoring…</Badge>
        )}
      </div>
      {a.fit?.summary && <p className="text-xs text-muted-foreground">{a.fit.summary}</p>}
      {a.note && <p className="text-xs italic border-l-2 border-primary/40 pl-2">“{a.note}”</p>}
      <div className="flex items-center gap-2 text-muted-foreground">
        {c.email && <a href={`mailto:${c.email}`} data-testid={`applicant-email-${a.id}`}><Mail className="h-3.5 w-3.5 hover:text-foreground" /></a>}
        {c.linkedin && <a href={c.linkedin} target="_blank" rel="noreferrer"><Linkedin className="h-3.5 w-3.5 hover:text-foreground" /></a>}
        <Button variant="ghost" size="sm" className="ml-auto h-7 px-2 text-xs" onClick={() => onScore(a.id)} disabled={scoring} data-testid={`applicant-rescore-${a.id}`}>
          <RefreshCw className={`h-3 w-3 mr-1 ${scoring ? "animate-spin" : ""}`} />Score
        </Button>
      </div>
      <Select value={a.status} onValueChange={(v) => onStage(a.id, v)}>
        <SelectTrigger className="h-8 text-xs capitalize" data-testid={`applicant-stage-${a.id}`}><SelectValue /></SelectTrigger>
        <SelectContent>{stages.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
      </Select>
    </Card>
  );
};

export const ApplicantPipeline = ({ roleId }) => {
  const [data, setData] = useState(null);
  const [scoring, setScoring] = useState(null);
  const load = () => api.get(`/roles/${roleId}/applicants`).then((r) => setData(r.data)).catch(() => toast.error("Could not load applicants"));
  useEffect(() => { load(); }, [roleId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onStage = async (id, stage) => {
    setData((d) => ({ ...d, items: d.items.map((a) => (a.id === id ? { ...a, status: stage } : a)) }));
    try { await api.patch(`/applications/${id}/stage`, { stage }); toast.success(`Moved to ${stage}`); }
    catch { toast.error("Update failed"); load(); }
  };
  const onScore = async (id) => {
    setScoring(id);
    try { const { data: fit } = await api.post(`/applications/${id}/score`); setData((d) => ({ ...d, items: d.items.map((a) => (a.id === id ? { ...a, fit } : a)) })); }
    catch { toast.error("Scoring failed"); }
    finally { setScoring(null); }
  };

  if (!data) return null;
  return (
    <section className="space-y-4" data-testid="applicant-pipeline">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-medium flex items-center gap-2"><Users className="h-5 w-5 text-primary" />Applicant pipeline</h2>
        <span className="text-xs text-muted-foreground font-mono" data-testid="applicant-total">{data.items.length} applicant{data.items.length === 1 ? "" : "s"} · scroll for all stages →</span>
      </div>
      {data.items.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground text-sm" data-testid="applicants-empty">No applicants yet. Share this role to start the pipeline.</Card>
      ) : (
        <div className="grid grid-flow-col auto-cols-[minmax(185px,1fr)] gap-3 overflow-x-auto pb-2">
          {data.stages.map((s) => {
            const items = data.items.filter((a) => a.status === s);
            return (
              <div key={s} className="rounded-xl bg-secondary/40 p-3 space-y-3 min-h-[160px]" data-testid={`stage-col-${s}`}>
                <div className="label-caps flex justify-between capitalize"><span>{s}</span><span className="font-mono">{items.length}</span></div>
                {items.map((a) => <ApplicantCard key={a.id} a={a} stages={data.stages} onStage={onStage} onScore={onScore} scoring={scoring === a.id} />)}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};
